import { ValueGetterParams } from 'ag-grid-community';
import { every, get, head, includes, isEmpty, isEqual, some } from 'lodash';

import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';

import { rx } from '@app/shared/rx/rx-wrapper';
import {
  FieldList,
  IFormGroupRules,
  IFormRules,
  ValidationService,
} from '@components/form/services';
import {
  BUTTON_FLEX_ORDER,
  CELL_FILTER,
  CELL_RENDERER,
  COLUMN_SELECTOR_ID,
  COMMON_ACTION_TYPES,
  COMMON_ACTION_TYPES_REJECT,
  DROPDOWN_MENU_POSITION,
  ENTITY_TYPES,
  ITransferData,
  TOOLBAR_BUTTON_POSITION,
  TRANSFER_STATUS_ENUM_OPTIONS,
  TRANSFER_TYPE,
  TRANSFER_TYPE_ENUM_OPTIONS,
  WIDGET_COMPONENT,
  XUX_GRID_ACTIONS,
} from '@shared/types';
import { alphanumericComparator } from '@app/shared/common/alpha-numeric.comparator/alpha-numeric.comparator';
import { stringCaseInsensitiveComparator } from '@app/shared/common/string-case-insensitive.comparator/string-case-insensitive.comparator';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import {
  ACTIONS_PANEL_BUTTON,
  BTN_ADD_SITES,
  BTN_CANCEL,
  BTN_REMOVE,
  GRID_EDIT_BTN,
} from '@app/text-content/button-templates/button-templates-content';
import { isHiddenRemoveButton, getListGridRowCellClassForGroup } from './utils';
import {
  TRANSFER_STATUS_OPTIONS,
  TRANSFER_STATUS_VALUE,
} from '@app/shared/types/enums/transfer-status.enum';
import { WIDGET_FAMILY } from '@config/global-constants';
import { MAX_GROUP_NAME_LENGTH_LIMIT } from './transfer-group.constants';
import { TransferGroupEditContainerComponent } from '@app/test/shared/editors-containers';

import {
  DISPLAY_EVENT_TYPES,
  TRANSFER_FINAL_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '../transfer/transfer.definition';
import { TransferGroupFormService } from './transfer-group.form.service';
import { COMMON_ICONS_COLUMN } from '@shared/constants/common-warn-grid-setting';
import { UI_DIMENSIONS } from '@config/ui-dimensions';
import {
  BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
  BTN_BULK_REPLENISH_QOH,
  BTN_BULK_REPLENISH_ZERO_QOH,
  GROUP_BTN_BULK_REPLENISH_BASE,
} from '../transfer-replenish/transfer-replenish.constants';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { getObservableValue } from '@app/shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services/xvi-translate.service';

export const createFormRules = (
  formService: TransferGroupFormService,
  editor: TransferGroupEditContainerComponent,
): IFormRules => {
  const i18nService = formService.i18nService as XVII18nService;
  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    grand_totals_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
    transfer_destinations_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: [
        'general_section',
        'grand_totals_section',
        'transfer_destinations_section',
      ],
    },
    {
      id: 'general_section',
      fields: [
        'transfer_type',
        'name',
        'event_id',
        'site_id',
        'transaction_date',
        'virtual.transaction_date',
      ],
    },
    {
      id: 'grand_totals_section',
      fields: ['virtual.grand_totals'],
    },
    {
      id: 'transfer_destinations_section',
      fields: [
        'event_expired_warning',
        'virtual.transfer_items',
        'virtual.really_changed_transfers',
      ],
    },
  ];

  const topSectionFields: FieldList<string> = {
    // this is for duplicate name check entity id
    entity_id: {
      show: false,
    },
    transfer_type: {
      title: I18n.Common.Type,
      widget: WIDGET_COMPONENT.select,
      optionText: TRANSFER_TYPE_ENUM_OPTIONS,
      defaultValue: TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER,
      disabled: true,
    },
    name: {
      title: I18n.Common.Name,
      widgetFamily: WIDGET_FAMILY.XUX,
      widget: WIDGET_COMPONENT.input,
      widgetData: {
        placeholder: I18n.Forms.TransferForm.EnterTransferGroupName,
      },
      required: false,
      maxLength: MAX_GROUP_NAME_LENGTH_LIMIT,
      pattern: {
        value: ValidationService.PATTERNS.STRING_WITHOUT_SPECIAL_CHARACTERS,
        errorMessage: i18nService.translate(
          I18n.ErrorMessages.SpecialCharactersAreNotAllowed,
        ),
      },
      disabled: !editor.canManage,
    },
    event_id: {
      title: I18n.EntityNames.Event,
      widget: WIDGET_COMPONENT.entity_select,
      required: true,
      disabledObs: formService.isTransferItemsNotEmpty$.pipe(
        rx.map(
          (transferItemsNotEmpty) => !editor.canManage || transferItemsNotEmpty,
        ),
      ),
      widgetData: {
        entity: ENTITY_TYPES.EVENTS,
        dataSource: formService.eventIdDataSource,
        lazy: true,
        hideEmptyOption: true,
        enableClientFiltering: true,
      },
      show: ({ transfer_type }): boolean =>
        includes(DISPLAY_EVENT_TYPES, transfer_type),
      omit: ({ transfer_type }): boolean =>
        !includes(DISPLAY_EVENT_TYPES, transfer_type),
      defaultValue: null,
      validators: [formService.validationService.requiredValidator],
    },
    site_id: {
      title: I18n.Common.From,
      widget: WIDGET_COMPONENT.entity_select,
      required: true,
      disabledObs: formService.isTransferItemsNotEmpty$.pipe(
        rx.map(
          (transferItemsNotEmpty) => !editor.canManage || transferItemsNotEmpty,
        ),
      ),
      widgetData: {
        entity: ENTITY_TYPES.SITE,
        dataSource: formService.siteIdDataSource,
        lazy: true,
        enableClientFiltering: true,
      },
      validators: [formService.validationService.requiredValidator],
    },
    transaction_date: {
      title: I18n.Common.Date,
      widget: WIDGET_COMPONENT.xux_datepicker,
      widgetData: {
        minimumObs: formService.transactionDateLimit.minimumObs,
        maximumObs: formService.transactionDateLimit.maximumObs,
      },
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          // Get the current transfer type from the form
          const currentTransferType = editor.form?.get('transfer_type')?.value;
          const isPreEventTransfer =
            currentTransferType === TRANSFER_TYPE.PRE_EVENT_TRANSFER;

          return formService.getTransactionDateRangeValidator(
            getObservableValue(formService.transactionDateLimit.maximumObs),
            getObservableValue(formService.transactionDateLimit.minimumObs),
            isPreEventTransfer,
          )(control);
        },
      ],
      disabledObs: formService.isDisableTransferDateObs.pipe(
        rx.map((disableDate) => !editor.canManage || disableDate),
      ),
    },
    'virtual.transaction_date': {
      show: false,
    },
  };

  const grandTotalsSectionFields: FieldList<string> = {
    'virtual.grand_totals': {
      widget: WIDGET_COMPONENT.edit_grid_custom,
      title: I18n.Common.GrandTotals,
      class: 'navigate-grid',
      widgetData: {
        filterable: false,
        showSearch: false,
        canAdd: false,
        canEdit: false,
        orderControls: false,
        canRemove: false,
        hideActionPanel: true,
        disableColumnSelector: true,
        multiselect: false,
        columns: [
          {
            path: 'department_code',
            title: I18n.EntityNames.DepartmentCode,
            sortable: true,
            defaultView: true,
            flex: 1,
            comparator: stringCaseInsensitiveComparator,
          },
          {
            path: 'request_cost',
            title: I18n.Forms.TransferForm.RequestCost,
            sortable: true,
            defaultView: true,
            flex: 1,
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(true),
            comparator: (valueA, valueB): number =>
              alphanumericComparator(valueA, valueB),
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'reversed_cost',
            title: I18n.Forms.TransferForm.ReversedCost,
            sortable: true,
            defaultView: true,
            flex: 1,
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(true),
            comparator: (valueA, valueB): number =>
              alphanumericComparator(valueA, valueB),
            ...COLUMN_ALIGN_RIGHT,
          },
          {
            path: 'received_cost',
            title: I18n.Forms.TransferForm.ReceivedCost,
            sortable: true,
            defaultView: true,
            flex: 1,
            valueFormatter:
              formService.numberFormatService.getNumberFormatter(true),
            comparator: (valueA, valueB): number =>
              alphanumericComparator(valueA, valueB),
            ...COLUMN_ALIGN_RIGHT,
          },
        ],
        eventHandlers: {
          [XUX_GRID_ACTIONS.GRID_READY]:
            formService.handleGrandTotalGridReady.bind(formService),
        },
      },
    },
  };

  const destinationSectionFields: FieldList<string> = {
    'virtual.transfer_items': {
      widget: WIDGET_COMPONENT.edit_grid,
      title: I18n.EntityNames.TransferDestinations,
      class: 'no-padding',
      titleTipsObs: formService.transferDestinationsTitleTips$,
      descriptionObs: formService.transferDestinationsDescription$,
      suppressNoPermissionsDisable: true,
      action: formService.virtualTransferItemsAction$,
      widgetData: {
        displayCountObs: formService.transferDestinationsDisplayCount$,
        selectedCountObs: formService.transferDestinationsSelectedCount$,
        useCustomButtonsOnFirstPosition: true,
        showSearch: true,
        multiselect: true,
        orderControls: false,
        disableColumnSelector: true,
        canRemove: true,
        showRowOptions: true,
        gridHeight: '600px',
        filterBeforeUpdateItems: true,
        userGridOptions: {
          overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
          rowClassRules: getListGridRowCellClassForGroup(),
        },
        buttons: [
          {
            ...GROUP_BTN_BULK_REPLENISH_BASE,
            position: TOOLBAR_BUTTON_POSITION.HEADER,
            class: 'btn-primary np-non-effect',
            dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
            disabledObs: formService.bulkReplenishToPARDisabled$.pipe(
              rx.map(
                (disableBulkReplenish) =>
                  !editor.canAdd || !editor.canManage || disableBulkReplenish,
              ),
            ),
            hiddenObs: formService.hideRelatedButtonsOfInvalidEvent$,
            childItems: [
              {
                ...BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                hidden: () => {
                  return !formService.isPreEventTypeTransfer();
                },
                click: formService
                  .getBulkReplenishClickHandler(
                    BTN_BULK_REPLENISH_ENDING_COUNT_OR_EXPECTED_QTY,
                  )
                  .bind(formService),
              },
              {
                ...BTN_BULK_REPLENISH_QOH,
                click: formService
                  .getBulkReplenishClickHandler(BTN_BULK_REPLENISH_QOH)
                  .bind(formService),
              },
              {
                ...BTN_BULK_REPLENISH_ZERO_QOH,
                click: formService
                  .getBulkReplenishClickHandler(BTN_BULK_REPLENISH_ZERO_QOH)
                  .bind(formService),
              },
            ],
          },
          {
            ...BTN_ADD_SITES,
            flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
            position: TOOLBAR_BUTTON_POSITION.HEADER,
            dropdownMenuPosition: DROPDOWN_MENU_POSITION.RIGHT,
            disabledObs: formService.disableAddSiteObs.pipe(
              rx.map(
                (addSiteDisable) =>
                  !editor.canAdd || !editor.canManage || addSiteDisable,
              ),
            ),
            click: formService.openSiteSelector.bind(formService),
            hiddenObs: formService.hideRelatedButtonsOfInvalidEvent$,
          },
          {
            ...ACTIONS_PANEL_BUTTON,
            flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
            hidden: (selectedItems): boolean => {
              return isEmpty(selectedItems);
            },
            childItems: [
              {
                ...GRID_EDIT_BTN,
                value: null,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !editor.canManage ||
                  get(selectedItems, 'length') > 1 ||
                  includes(TRANSFER_FINAL_STATUS, head(selectedItems)?.status),
                click: (data): Promise<void> =>
                  formService.editSiteInventoryItems(head(data)),
              },
              {
                ...GRID_EDIT_BTN,
                value: null,
                caption: I18n.Common.View,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  get(selectedItems, 'length') > 1 ||
                  (editor.canManage &&
                    !includes(
                      TRANSFER_FINAL_STATUS,
                      head(selectedItems)?.status,
                    )),
                click: (data): Promise<void> =>
                  formService.editSiteInventoryItems(head(data)),
              },
              {
                value: COMMON_ACTION_TYPES.Submit,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.Submit,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !editor.canManage ||
                  !every(selectedItems, (item) =>
                    includes(
                      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.New],
                      item.status,
                    ),
                  ),
                click: (data): Promise<any> =>
                  formService.bulkUpdateTransfer(
                    data,
                    COMMON_ACTION_TYPES.Submit,
                    editor.sendSidebarDetailDoc.bind(editor),
                  ),
              },
              {
                ...BTN_CANCEL,
                class: 'xux-action-button btn full-width',
                caption: I18n.Forms.TransferForm.CancelTransfer,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !editor.canManage ||
                  some(
                    selectedItems,
                    (item) => !isEqual(item.status, TRANSFER_STATUS_VALUE.Open),
                  ),
                click: (data): Promise<any> =>
                  formService.bulkUpdateTransfer(
                    data,
                    COMMON_ACTION_TYPES.Cancel,
                    editor.sendSidebarDetailDoc.bind(editor),
                  ),
              },
              {
                value: COMMON_ACTION_TYPES.Accept,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.Accept,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !editor.canManage ||
                  some(
                    selectedItems,
                    (item) =>
                      !isEqual(item.status, TRANSFER_STATUS_VALUE.Pending) ||
                      formService.isPreEventTypeTransfer(),
                  ),
                click: (data): Promise<any> =>
                  formService.bulkUpdateTransfer(
                    data,
                    COMMON_ACTION_TYPES.Accept,
                    editor.sendSidebarDetailDoc.bind(editor),
                  ),
              },
              {
                value: COMMON_ACTION_TYPES_REJECT,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.Reject,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !editor.canManage ||
                  some(
                    selectedItems,
                    (item) =>
                      !isEqual(item.status, TRANSFER_STATUS_VALUE.Pending),
                  ),
                click: (data): Promise<any> =>
                  formService.bulkUpdateTransfer(
                    data,
                    COMMON_ACTION_TYPES_REJECT,
                    editor.sendSidebarDetailDoc.bind(editor),
                  ),
              },

              {
                value: COMMON_ACTION_TYPES.Print,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.Print,
                hidden(selectedItems: ITransferData[]): boolean {
                  return !(
                    selectedItems.length &&
                    !some(selectedItems, (item) =>
                      includes(TRANSFER_UNSAVED_STATUS, item.status),
                    )
                  );
                },
                click: formService
                  .getPrintOrExportClickHandler(false, false)
                  .bind(formService),
              },
              {
                value: COMMON_ACTION_TYPES.Export,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.ExportPDF,
                hidden(selectedItems: ITransferData[]): boolean {
                  return !(
                    selectedItems.length &&
                    !some(selectedItems, (item) =>
                      includes(TRANSFER_UNSAVED_STATUS, item.status),
                    )
                  );
                },
                click: formService
                  .getPrintOrExportClickHandler(false, true)
                  .bind(formService),
              },
              {
                value: COMMON_ACTION_TYPES.PrintWrittenSheet,
                class: 'xux-action-button btn full-width',
                caption: I18n.Common.PrintWrittenSheet,
                hidden(selectedItems: ITransferData[]): boolean {
                  return !(
                    selectedItems.length &&
                    every(selectedItems, (item) =>
                      includes(
                        [
                          TRANSFER_STATUS_VALUE.Pending,
                          TRANSFER_STATUS_VALUE.Open,
                        ],
                        item.status,
                      ),
                    )
                  );
                },
                click: formService
                  .getPrintOrExportClickHandler(true, false)
                  .bind(formService),
              },
              {
                value: COMMON_ACTION_TYPES.Void,
                class: 'xux-action-button btn full-width',
                caption: I18n.Status.Void,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  !formService.canBulkVoid.call(
                    formService,
                    selectedItems,
                    true,
                  ),
                click: formService.bulkVoidTransfer.bind(
                  formService,
                  editor.sendSidebarDetailDoc.bind(editor),
                ),
              },
              {
                ...BTN_REMOVE,
                hidden: (selectedItems: ITransferData[]): boolean =>
                  some(selectedItems, isHiddenRemoveButton),
                class: 'xux-action-button btn full-width',
              },
            ],
          },
          {
            caption: (item): string =>
              i18nService.instant(
                editor.canManage &&
                  !includes(TRANSFER_FINAL_STATUS, item.status)
                  ? I18n.Common.Edit
                  : I18n.Common.View,
              ),
            click: (value: string, gridItem: ITransferData): Promise<void> =>
              formService.editSiteInventoryItems(gridItem),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            ...BTN_REMOVE,
            hidden: (transfers): boolean =>
              isHiddenRemoveButton(head(transfers)),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Common.Submit,
            hidden: ([transfer]: ITransferData[]): boolean =>
              !editor.canManage ||
              !includes(
                [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.New],
                transfer?.status,
              ),
            click: (_, gridItem: ITransferData): Promise<any> =>
              formService.bulkUpdateTransfer(
                [gridItem],
                COMMON_ACTION_TYPES.Submit,
                editor.sendSidebarDetailDoc.bind(editor),
              ),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Forms.TransferForm.CancelTransfer,
            click: (value: string, gridItem: ITransferData): Promise<boolean> =>
              formService.bulkUpdateTransfer(
                [gridItem],
                COMMON_ACTION_TYPES.Cancel,
                editor.sendSidebarDetailDoc.bind(editor),
              ),
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !editor.canManage ||
                !currentItem ||
                !isEqual(currentItem.status, TRANSFER_STATUS_VALUE.Open)
              );
            },
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Common.Accept,
            click: (value: string, gridItem: ITransferData): Promise<boolean> =>
              formService.bulkUpdateTransfer(
                [gridItem],
                COMMON_ACTION_TYPES.Accept,
                editor.sendSidebarDetailDoc.bind(editor),
              ),
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !editor.canManage ||
                !currentItem ||
                !isEqual(currentItem.status, TRANSFER_STATUS_VALUE.Pending) ||
                formService.isPreEventTypeTransfer()
              );
            },
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Common.Reject,
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !editor.canManage ||
                !currentItem ||
                !isEqual(currentItem.status, TRANSFER_STATUS_VALUE.Pending)
              );
            },
            click: (value: string, gridItem: ITransferData): Promise<boolean> =>
              formService.bulkUpdateTransfer(
                [gridItem],
                COMMON_ACTION_TYPES_REJECT,
                editor.sendSidebarDetailDoc.bind(editor),
              ),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },

          {
            caption: I18n.Common.Print,
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !currentItem ||
                includes(TRANSFER_UNSAVED_STATUS, currentItem.status)
              );
            },
            click: (value: string, gridItem: ITransferData): Promise<void> =>
              formService.printOrExport(gridItem, COMMON_ACTION_TYPES.Print),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Common.ExportPDF,
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !currentItem ||
                includes(TRANSFER_UNSAVED_STATUS, currentItem.status)
              );
            },
            click: (value: string, gridItem: ITransferData): Promise<void> =>
              formService.printOrExport(gridItem, COMMON_ACTION_TYPES.Export),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Common.PrintWrittenSheet,
            hidden: (transfers: ITransferData[]): boolean => {
              const currentItem = head(transfers);

              return (
                !currentItem ||
                !includes(
                  [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
                  currentItem.status,
                )
              );
            },
            click: (value: string, gridItem: ITransferData): Promise<void> =>
              formService.getPrintOrExportClickHandler(true, false)([gridItem]),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
          {
            caption: I18n.Status.Void,
            hidden: (transfers: ITransferData[]): boolean =>
              !formService.canVoid(head(transfers), false),
            click: (value: string, gridItem: ITransferData): void =>
              formService.bulkVoidTransfer.call(
                formService,
                editor.sendSidebarDetailDoc.bind(editor),
                [gridItem],
              ),
            position: TOOLBAR_BUTTON_POSITION.ROW,
          },
        ],
        columns: [
          {
            ...COMMON_ICONS_COLUMN,
            width: UI_DIMENSIONS.columnWidthIcon,
            minWidth: UI_DIMENSIONS.columnWidthIcon,
            cellRendererParams: formService.getDMSyncIconRendererParams.bind(
              formService,
            )(formService.formFieldPath.siteIdTo),
          },
          {
            path: 'store_number_to',
            title: I18n.Sites.SiteId,
            sortable: true,
            comparator: stringCaseInsensitiveComparator,
            defaultView: true,
            filterParams: {
              quickFilter: true,
            },
            cellRenderer: editor.canManage ? CELL_RENDERER.link_renderer : null,
            cellRendererParams: {
              onClick: (data): Promise<void> =>
                formService.editSiteInventoryItems(data),
            },
          },
          {
            path: 'site_name_to',
            title: I18n.Sites.SiteName,
            sortable: true,
            defaultView: true,
            comparator: stringCaseInsensitiveComparator,
            filterParams: {
              quickFilter: true,
            },
            cellRenderer: editor.canManage ? CELL_RENDERER.link_renderer : null,
            cellRendererParams: {
              onClick: (data): Promise<void> =>
                formService.editSiteInventoryItems(data),
            },
          },
          {
            path: 'status',
            title: I18n.Common.Status,
            sortable: true,
            defaultView: true,
            filterType: CELL_FILTER.enum,
            filterParams: {
              values: TRANSFER_STATUS_OPTIONS,
            },
            cellRenderer: CELL_RENDERER.prepared_string_renderer,
            cellRendererParams: {
              valueToEnumMap: TRANSFER_STATUS_ENUM_OPTIONS,
            },
            valueGetter: (params: ValueGetterParams): string => {
              const statusValue = params.data?.status;

              return isEqual(statusValue, TRANSFER_STATUS_VALUE.Null)
                ? ''
                : statusValue;
            },
          },
          {
            path: COLUMN_SELECTOR_ID,
            width: 50,
            maxWidth: 50,
            title: '',
            defaultView: true,
            pinned: detectMobile() ? false : 'right',
            lockPinned: true,
          },
        ],
        composeConfirmationModalOptions:
          formService.composeConfirmationModalOptions.bind(formService),
        eventHandlers: {
          [XUX_GRID_ACTIONS.GRID_READY]:
            formService.handleDestinationGridAction.bind(formService),
          [XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED]:
            formService.handleDestinationGridAction.bind(formService),
        },
      },
    },
    'virtual.really_changed_transfers': {
      show: false,
      defaultValue: {},
    },
  };

  return {
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        transaction_date: {
          type: ['string', 'object'],
        },
        selected_event: {
          type: 'string',
        },
        grand_totals: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              department_code: { type: 'string' },
              request_cost: { type: 'number' },
              reversed_cost: { type: 'number' },
              received_cost: { type: 'number' },
            },
          },
        },
        transfer_items: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              item_count: { type: 'number' },
              total_cost: { type: 'number' },
            },
          },
        },
        really_changed_transfers: {
          type: 'object',
        },
      },
    },
    groups: [...panelGroups],
    fields: {
      ...sections,
      ...topSectionFields,
      ...grandTotalsSectionFields,
      ...destinationSectionFields,
    },
  };
};
